import { Alert, Box, CircularProgress, useTheme } from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { RobotoDomainException } from "@/shared/http";
import { useVizState } from "@/shared/state/visualization";

import { DropZone, DropZones, Droppable } from "../DnD";
import { Layout } from "../Layout";
import { ViewportProvider } from "../Panel/PlotPanel/Viewport/ViewportProvider";
import { PanelResizeHandle } from "../resize-handles";
import { useEphemeralWorkspaceState } from "../WorkspaceCtx/hooks";

import styles from "./PanelBoard.module.css";
import { PanelOptionsGrid } from "./PanelOptionsGrid";

interface PanelBoardProps {
  className?: classNames.Argument;
  errorMsg?: string;
  style?: React.CSSProperties;
}

export function PanelBoard(props: PanelBoardProps) {
  const { errorMsg } = props;

  const theme = useTheme();
  const { isHydrating } = useEphemeralWorkspaceState();
  const state = useVizState();

  const rootRef = React.useRef<HTMLDivElement>(null);

  const layouts = React.useMemo(() => {
    const rootLayout = state.layout;
    const layouts = [];

    if (rootLayout.children.length > 0) {
      const firstChild = rootLayout.children[0];
      layouts.push(<Layout key={firstChild.id} layout={firstChild} />);
    }

    for (let i = 1; i < rootLayout.children.length; i++) {
      const prevLayout = rootLayout.children[i - 1];
      const currentLayout = rootLayout.children[i];

      layouts.push(
        <PanelResizeHandle
          key={prevLayout.id + currentLayout.id}
          axis={rootLayout.axis}
          containerRef={rootRef}
          layout1={prevLayout}
          layout2={currentLayout}
        />,
      );

      layouts.push(<Layout key={currentLayout.id} layout={currentLayout} />);
    }

    return layouts;
  }, [state]);

  if (errorMsg) {
    return (
      <div className={styles.error}>
        <Alert severity="error">{errorMsg}</Alert>
      </div>
    );
  }

  const rootLayout = state.layout;

  if (isHydrating) {
    return (
      <div className={classNames(props.className)} style={props.style}>
        <LoadingIndicator />
      </div>
    );
  }

  if (rootLayout.children.length === 0) {
    return (
      <Droppable
        dropZone={DropZone.PanelBoard}
        droppableId="panelBoard"
        className={classNames(styles.emptyBoard, props.className)}
        style={props.style}
      >
        <LayoutNotFoundWarning />
        <PanelOptionsGrid />
      </Droppable>
    );
  }

  return (
    <ViewportProvider>
      <Box
        bgcolor={theme.palette.code.main}
        className={classNames(styles.panelBoard, props.className)}
        style={props.style}
      >
        <div
          ref={rootRef}
          className={classNames(styles.flexContainer, {
            [styles.flexCol]: rootLayout.axis === "y",
          })}
        >
          {layouts}
          {rootLayout.children.length > 1 && (
            <DropZones
              idPrefix="panelBoard"
              dropZone={DropZone.Layout}
              axis={rootLayout.axis}
              data={rootLayout}
            />
          )}
        </div>
      </Box>
    </ViewportProvider>
  );
}

function LayoutNotFoundWarning() {
  const { layoutError } = useEphemeralWorkspaceState();
  const [dismissed, setDismissed] = React.useState(false);

  if (!layoutError || dismissed) {
    return null;
  }

  let message =
    "Failed to load layout. Refresh the page, select a different layout, or create a new one.";

  if (
    layoutError instanceof RobotoDomainException &&
    layoutError.isClientError()
  ) {
    message = layoutError.message;
  }

  return (
    <Alert severity="warning" onClose={() => setDismissed(true)}>
      {message}
    </Alert>
  );
}

function LoadingIndicator() {
  const theme = useTheme();
  return (
    <Box
      bgcolor={theme.palette.background.default}
      className={styles.loadingContainer}
    >
      <CircularProgress />
      <div className={styles.loadingText}>
        <RobotoTypography variant="h6" gutterBottom>
          Loading files...
        </RobotoTypography>
        <RobotoTypography variant="body2" color="text.secondary">
          Once loaded, drag and drop topic data from the sidebar to visualize
          it.
        </RobotoTypography>
      </div>
    </Box>
  );
}
